import React, { ReactElement, useState, useEffect } from 'react';
import styled from 'styled-components';
import useGenerateApi from '../hooks/useGenerateApi';
import type { Issue, GenerateApiRequest, AppData } from '../types';
import Card from './common/Card';
import CardCTA from './common/CardCTA';
import OpenCustomerQuestionsDrawer from './OpenCustomerQuestionsDrawer';
import { Icon } from 'bench-internal-theme';

type OpenCustomerQuestionsCardProps = {
  appData: AppData;
};

const Container = styled.div`
  max-height: 512px;
  overflow-y: auto;
  padding: 0;
  word-wrap: break-word;
`;

const Question = styled.div`
  padding: 20px;
  border-top: 2px solid #e4e5e7;
  &:first-of-type {
    border: none;
  }
`;

const IssueName = styled.div`
  font-weight: bold;
  margin-bottom: 10px;
`;

const IssueDescription = styled.div`
  margin-bottom: 10px;
`;

const HeaderIcon = styled(Icon)`
  transform: rotate(180deg);
`;

const OpenCustomerQuestionsCard = ({ appData }: OpenCustomerQuestionsCardProps): ReactElement => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue>();
  const [generatedMessage, setGeneratedMessage] = useState<string>('');
  const requestData: GenerateApiRequest = {
    messages: appData.recentMessages,
    issue: selectedIssue,
    instructions: '',
  };
  const { message, loading, onSubmit, onUpdate, clearMessage } = useGenerateApi(
    appData.clientId,
    requestData,
  );

  useEffect(() => {
    setGeneratedMessage(message || '');
  }, [message]);
  return (
    <Card
      header={'Open Customer Questions'}
      headerIcon={<HeaderIcon icon={'information'} />}
      defaultExpanded={true}
    >
      {showDrawer ? (
        <OpenCustomerQuestionsDrawer
          open={showDrawer}
          onClose={(): void => {
            setShowDrawer(false);
            clearMessage();
          }}
          issue={selectedIssue}
          onSubmit={onSubmit}
          onUpdate={onUpdate}
          generatedMessage={generatedMessage}
          loading={loading}
        />
      ) : (
        <div></div>
      )}
      <Container>
        {appData.unresolvedIssues?.map((issue: Issue, i: number) => (
          <Question key={i}>
            <IssueName>{issue.issueTopic}</IssueName>
            <IssueDescription>{issue.description}</IssueDescription>
            <CardCTA
              label={'Generate a response'}
              onClick={(): void => {
                setShowDrawer(true);
                setSelectedIssue(issue);
              }}
            />
          </Question>
        ))}
      </Container>
    </Card>
  );
};

export default OpenCustomerQuestionsCard;
